interface WsHandlers {
  onOpen?: () => void;
  onMessage?: (data: unknown) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
}

export interface WsConnection {
  send: (data: unknown) => void;
  close: () => void;
}

const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 10_000;

export function createWebSocket(
  path: string,
  handlers: WsHandlers = {},
): WsConnection {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${proto}//${window.location.host}${wsPath}`;

  let ws: WebSocket | null = null;
  let closed = false;
  let backoff = INITIAL_BACKOFF_MS;
  let reconnectTimer: number | null = null;

  const connect = (): void => {
    if (closed) return;
    ws = new WebSocket(url);

    ws.addEventListener('open', () => {
      backoff = INITIAL_BACKOFF_MS;
      handlers.onOpen?.();
    });

    ws.addEventListener('message', (event) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        parsed = event.data;
      }
      handlers.onMessage?.(parsed);
    });

    ws.addEventListener('close', (event) => {
      handlers.onClose?.(event);
      if (!closed) {
        reconnectTimer = window.setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      }
    });

    ws.addEventListener('error', (event) => {
      handlers.onError?.(event);
    });
  };

  connect();

  return {
    send: (data: unknown) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        const payload = typeof data === 'string' ? data : JSON.stringify(data);
        ws.send(payload);
      }
    },
    close: () => {
      closed = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      ws?.close();
    },
  };
}
